import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import "./Filters3.css";

const Filters3 = ({ onFetch }) => {
  const [allData, setAllData] = useState([]);
  const [pidList, setPidList] = useState([]);
  const [managerList, setManagerList] = useState([]);

  const [selectedPid, setSelectedPid] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [initialLoad, setInitialLoad] = useState(true);

  /* ================= HELPER TO HANDLE ARRAY OR STRING ================= */

  const getStoredValue = (key) => {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    try {
      const parsed = JSON.parse(stored);

      // If array → take first value
      if (Array.isArray(parsed)) {
        return parsed.length > 0 ? parsed[0] : null;
      }

      return parsed;
    } catch {
      // If plain string (not JSON)
      return stored;
    }
  };

  /* ================= INITIAL LOAD ================= */

  useEffect(() => {
    axios
      .get("https://api-usersreport-insights.haqdarshak.com/api/team-view-achievement-table-all")
      .then((res) => {
        const data = res.data;
        setAllData(data);

        const uniquePIDs = [
          ...new Set(data.map((item) => item.Organization_PID)),
        ].filter(Boolean);

        const uniqueManagers = [
          ...new Set(data.map((item) => item.Manager_Name)),
        ].filter(Boolean);

        const pidOptions = uniquePIDs.map((pid) => ({
          value: pid,
          label: pid,
        }));

        const managerOptions = uniqueManagers.map((m) => ({
          value: m,
          label: m,
        }));

        setPidList(pidOptions);
        setManagerList(managerOptions);

        // ✅ Get first value if stored as array
        const savedPid = getStoredValue("selectedPid");
        const savedManager = getStoredValue("selectedManagerName");

        if (savedManager) {
          const managerObj = { value: savedManager, label: savedManager };
          setSelectedManager(managerObj);

          const filteredPIDs = data
            .filter((item) => item.Manager_Name === savedManager)
            .map((item) => item.Organization_PID);

          const uniqueFilteredPIDs = [
            ...new Set(filteredPIDs),
          ].filter(Boolean);

          const updatedPidList = uniqueFilteredPIDs.map((pid) => ({
            value: pid,
            label: pid,
          }));

          setPidList(updatedPidList);

          if (
            savedPid &&
            updatedPidList.find((p) => p.value === savedPid)
          ) {
            setSelectedPid({ value: savedPid, label: savedPid });
          }
        } else if (savedPid) {
          if (pidOptions.find((opt) => opt.value === savedPid)) {
            setSelectedPid({ value: savedPid, label: savedPid });
          }
        }

        setInitialLoad(false);
      })
      .catch((err) => console.error("Failed to fetch initial data", err));
  }, []);

  /* ================= PID → FILTER MANAGER ================= */

  useEffect(() => {
    if (initialLoad) return;
    if (!selectedPid) return;

    const filteredManagers = allData
      .filter((item) => item.Organization_PID === selectedPid.value)
      .map((item) => item.Manager_Name);

    const uniqueManagers = [...new Set(filteredManagers)].filter(Boolean);

    const updatedManagerList = uniqueManagers.map((m) => ({
      value: m,
      label: m,
    }));

    setManagerList(updatedManagerList);

    if (!updatedManagerList.find((m) => m.value === selectedManager?.value)) {
      setSelectedManager(null);
    }
  }, [selectedPid]);

  /* ================= MANAGER → FILTER PID ================= */

  useEffect(() => {
    if (initialLoad) return;
    if (!selectedManager) return;

    const filteredPIDs = allData
      .filter((item) => item.Manager_Name === selectedManager.value)
      .map((item) => item.Organization_PID);

    const uniquePIDs = [...new Set(filteredPIDs)].filter(Boolean);

    const updatedPidList = uniquePIDs.map((pid) => ({
      value: pid,
      label: pid,
    }));

    setPidList(updatedPidList);

    if (!updatedPidList.find((p) => p.value === selectedPid?.value)) {
      setSelectedPid(null);
    }
  }, [selectedManager]);

  /* ================= APPLY ================= */

  const handleApply = () => {
    if (selectedPid)
      localStorage.setItem("selectedPid", selectedPid.value);

    if (selectedManager)
      localStorage.setItem(
        "selectedManagerName",
        selectedManager.value
      );

    onFetch({
      pid: selectedPid?.value || "",
      manager: selectedManager?.value || "",
      startDate,
      endDate,
    });
  };

  /* ================= CLEAR ================= */

  const handleClear = () => {
    setSelectedPid(null);
    setSelectedManager(null);
    setStartDate("");
    setEndDate("");

    localStorage.removeItem("selectedPid");
    localStorage.removeItem("selectedManagerName");

    const uniquePIDs = [
      ...new Set(allData.map((item) => item.Organization_PID)),
    ].filter(Boolean);

    const uniqueManagers = [
      ...new Set(allData.map((item) => item.Manager_Name)),
    ].filter(Boolean);

    setPidList(uniquePIDs.map((pid) => ({ value: pid, label: pid })));
    setManagerList(uniqueManagers.map((m) => ({ value: m, label: m })));

    onFetch({ pid: "", manager: "", startDate: "", endDate: "" });
  };

  /* ================= UI ================= */

  return (
    <div className="filters-container-v22">
      <div className="filter-group-v22">
        <label>Select PID</label>
        <Select
          options={pidList}
          value={selectedPid}
          onChange={setSelectedPid}
          placeholder="Select PID"
          isSearchable
        />
      </div>

      <div className="filter-group-v22">
        <label>Select Manager</label>
        <Select
          options={managerList}
          value={selectedManager}
          onChange={setSelectedManager}
          placeholder="Select Manager"
          isSearchable
        />
      </div>

      <div className="filter-group-v22">
        <label>Start Date</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      </div>

      <div className="filter-group-v22">
        <label>End Date</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </div>

      <div className="filter-buttons-v2">
        <button className="filter-button-v2" onClick={handleApply}>
          Apply Filters
        </button>

        <button className="clear-button-v2" onClick={handleClear}>
          Clear Filters
        </button>
      </div>

      <p style={{ marginTop: "-10px", fontSize: "14px", color: "gray" }}>
        📌 This data is from 1st April 2025
      </p>

      <p className="filter-note">
        * Select both filter to view below data.
      </p>
    </div>
  );
};

export default Filters3;
